import { useEffect, useRef, useState } from 'react';
import { Upload, Check, X, AlertTriangle, RefreshCw, Square, Eye, Loader2 } from 'lucide-react';
import { opsApi } from '../../api/client';

const MAX_BYTES = 10 * 1024 * 1024;
const BUSY_RETRY_MS = 1500;
const BUSY_MAX_TRIES = 80; // ≈ 2 min of waiting for the single server import slot

const LABEL = {
  queued: 'In wachtrij', uploading: 'Uploaden', parsing: 'Verwerken op server', succeeded: 'Geïmporteerd',
  duplicate: 'Duplicaat', review: 'Nazicht nodig', failed: 'Mislukt', unknown: 'Uitkomst onbekend', rejected: 'Geweigerd', stopped: 'Niet gestart',
};
const TONE = {
  succeeded: 'bg-emerald-50 text-emerald-800 border-emerald-200', duplicate: 'bg-amber-50 text-amber-800 border-amber-200',
  review: 'bg-amber-50 text-amber-800 border-amber-200', failed: 'bg-red-50 text-red-800 border-red-200',
  unknown: 'bg-red-50 text-red-800 border-red-200', rejected: 'bg-gray-100 text-gray-600 border-gray-200',
  uploading: 'bg-sky-50 text-sky-800 border-sky-200', parsing: 'bg-sky-50 text-sky-800 border-sky-200',
  queued: 'bg-gray-50 text-gray-600 border-gray-200', stopped: 'bg-gray-50 text-gray-500 border-gray-200',
};
const DONE = new Set(['succeeded', 'duplicate', 'review', 'failed', 'unknown', 'rejected', 'stopped']);

async function sha256Hex(file) {
  if (!window.crypto?.subtle) return null;
  const buf = await file.arrayBuffer();
  const hash = await window.crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function validate(file) {
  const isPdf = /\.pdf$/i.test(file.name) || (file.type || '').toLowerCase().includes('pdf');
  if (!isPdf) return 'Geen PDF-bestand';
  if (file.size > MAX_BYTES) return 'Groter dan 10 MB';
  if (file.size === 0) return 'Leeg bestand';
  return null;
}

function outcomeFromResponse(data) {
  if (data?.ok === false && data?.duplicate) {
    const kind = data.severity === 'hard' ? 'duplicate' : 'review';
    const label = data.matchType === 'same_file_hash' ? 'zelfde PDF al geïmporteerd' : data.matchType === 'exact_invoice_number' ? 'factuurnummer bestaat al' : 'mogelijk dezelfde factuur (datum/totaal/klant)';
    return { state: kind, message: `${label} — bestaande factuur ${data.existingNumber || ''}`.trim(), invoiceId: data.existingId, invoiceNumber: data.existingNumber };
  }
  if (data?.ok === false && data?.incomplete) {
    return { state: 'review', message: data.message || 'Onvolledig geïmporteerd — nazicht nodig', invoiceId: data.invoiceId, invoiceNumber: data.invoiceNumber, incomplete: true };
  }
  if (data?.ok === false) return { state: 'failed', message: data.message || 'Geweigerd door de server' };
  const review = data.parseStatus && data.parseStatus !== 'parsed';
  return { state: review ? 'review' : 'succeeded', message: review ? `Geïmporteerd, nazicht nodig (${data.parseStatus})` : 'Geïmporteerd — wacht op export (niet automatisch geëxporteerd)', invoiceId: data.invoiceId, invoiceNumber: data.invoiceNumber, warnings: data.parseWarnings || [] };
}

export function PdfImportQueue({ onImported, onOpenInvoice }) {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const stopRef = useRef(false);
  const inputRef = useRef(null);
  const itemsRef = useRef([]);
  itemsRef.current = items;
  const [dragOver, setDragOver] = useState(false);

  const update = (id, patch) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setItems((prev) => [...prev, ...files.map((file, i) => {
      const error = validate(file);
      return { id: `${Date.now()}-${i}-${file.name}`, file, name: file.name, size: file.size, state: error ? 'rejected' : 'queued', message: error || '', uploadPct: 0 };
    })]);
  };

  useEffect(() => {
    if (!running) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  const processOne = async (item) => {
    let tries = 0;
    for (;;) {
      update(item.id, { state: 'uploading', uploadPct: 0, message: '' });
      try {
        const { data } = await opsApi.accountingImportPdf(item.file, (ev) => {
          const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
          update(item.id, { uploadPct: pct, state: pct >= 100 ? 'parsing' : 'uploading' });
        });
        return outcomeFromResponse(data);
      } catch (err) {
        if (!err.response) return { state: 'unknown', message: 'Netwerkfout: de server antwoordde niet. Onbekend of de factuur is aangemaakt — gebruik "Controleer & opnieuw".' };
        if (err.response.status >= 500) return { state: 'unknown', message: `Serverfout (HTTP ${err.response.status}) na mogelijke verwerking — uitkomst onbekend, gebruik "Controleer & opnieuw".` };
        if (err.response.status === 409 && err.response.data?.detail === 'import_lock_lost') return { state: 'unknown', message: 'Serverslot verloren tijdens de import. Niet blind opnieuw uploaden — gebruik "Controleer & opnieuw" (controleert eerst of de factuur al bestaat).' };
        if (err.response.status === 409 && err.response.data?.detail === 'import_busy') {
          tries += 1;
          if (stopRef.current || tries > BUSY_MAX_TRIES) return { state: 'failed', message: 'Server bezig met een andere import (ander tabblad/gebruiker). Niet gestart — probeer later opnieuw. Blijft dit lang zo, dan is mogelijk een importslot achtergebleven na een crash: beheerprocedure nodig (geen automatische vrijgave).' };
          update(item.id, { state: 'queued', message: 'Wacht: server verwerkt een andere import…' });
          await new Promise((r) => setTimeout(r, BUSY_RETRY_MS));
          continue;
        }
        const detail = err.response.data?.detail;
        return { state: 'failed', message: typeof detail === 'string' && detail ? detail : `Geweigerd (HTTP ${err.response.status})` };
      }
    }
  };

  const runQueue = async (ids) => {
    if (running) return;
    setRunning(true); stopRef.current = false;
    let imported = 0;
    for (const id of ids) {
      const current = itemsRef.current.find((it) => it.id === id);
      if (!current || !['queued', 'failed', 'unknown'].includes(current.state)) continue;
      if (stopRef.current) { update(id, { state: 'stopped', message: 'Gestopt vóór start' }); continue; }
      const outcome = await processOne(current);
      update(id, { ...outcome, uploadPct: 100 });
      if (outcome.state === 'succeeded' || outcome.state === 'review') imported += 1;
    }
    setRunning(false);
    if (imported && onImported) onImported();
  };

  const start = () => runQueue(items.filter((it) => it.state === 'queued').map((it) => it.id));
  const retryOne = (id) => runQueue([id]);
  const reconcileAndRetry = async (item) => {
    update(item.id, { message: 'Controleren of dit bestand al geïmporteerd is…' });
    const sha = await sha256Hex(item.file);
    if (sha) {
      try {
        const { data } = await opsApi.accountingImportLookup(sha);
        if (data.found && data.complete) { update(item.id, { state: 'succeeded', message: `Was al geïmporteerd als ${data.invoiceNumber || data.invoiceId} (exportwachtrij aanwezig) — geen tweede import gedaan`, invoiceId: data.invoiceId, invoiceNumber: data.invoiceNumber }); return; }
        if (data.found) { update(item.id, { state: 'review', incomplete: true, message: `Onvolledig: factuur ${data.invoiceNumber || data.invoiceId} bestaat, maar de exportwachtrij-entry ontbreekt. Werk de bestaande factuur af via "Bekijk factuur" → "Approve" (maakt de export-entry aan, exporteert niet). Niet opnieuw geüpload.`, invoiceId: data.invoiceId, invoiceNumber: data.invoiceNumber }); return; }
      } catch (e) { update(item.id, { message: 'Controle mislukt; niet opnieuw geprobeerd.' }); return; }
    }
    runQueue([item.id]);
  };

  const total = items.length; const done = items.filter((it) => DONE.has(it.state)).length;
  const queuedCount = items.filter((it) => it.state === 'queued').length;
  const counts = items.reduce((a, it) => ({ ...a, [it.state]: (a[it.state] || 0) + 1 }), {});
  const allDone = total > 0 && done === total && !running;

  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4" data-testid="pdf-import-queue">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => inputRef.current?.click()} disabled={running} data-testid="pdf-import-select-btn"
          className="px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
          <Upload className="w-4 h-4" /> PDF's kiezen
        </button>
        <input ref={inputRef} type="file" accept=".pdf,application/pdf" multiple className="hidden" data-testid="pdf-import-input"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!running) addFiles(e.dataTransfer.files); }}
          data-testid="pdf-import-dropzone"
          className={`flex-1 min-w-[220px] text-sm text-gray-500 border border-dashed rounded-lg px-3 py-2 ${dragOver ? 'border-gray-900 bg-gray-50' : 'border-gray-300'}`}>
          …of sleep PDF-facturen hierheen. Eén factuur per keer verwerkt; niets wordt automatisch geëxporteerd.
        </div>
        {queuedCount > 0 && !running && (
          <button onClick={start} data-testid="pdf-import-start-btn" className="px-4 py-2.5 bg-emerald-700 text-white rounded-lg text-sm font-semibold hover:bg-emerald-800 flex items-center gap-2">
            <Check className="w-4 h-4" /> Start import ({queuedCount})
          </button>
        )}
        {running && (
          <button onClick={() => { stopRef.current = true; }} data-testid="pdf-import-stop-btn" className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50 flex items-center gap-2">
            <Square className="w-4 h-4" /> Stop na huidig bestand
          </button>
        )}
        {allDone && <button onClick={() => setItems([])} data-testid="pdf-import-clear-btn" className="text-sm text-gray-500 hover:text-gray-900 underline">Wachtrij leegmaken</button>}
      </div>

      {total > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-gray-700 mb-1">
            <span data-testid="pdf-import-progress-text">{done} van {total} bestanden afgehandeld{running ? ' — bezig…' : ''}</span>
            {running && <span className="text-xs text-gray-500">Vernieuwen of navigeren verliest deze wachtrij; al verwerkte facturen blijven bestaan.</span>}
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={done} aria-valuemax={total} data-testid="pdf-import-progress-bar">
            <div className="h-full bg-gray-900 transition-[width]" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
          </div>
          <ul className="mt-3 divide-y divide-gray-100" data-testid="pdf-import-list">
            {items.map((it) => (
              <li key={it.id} className="py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`pdf-import-item-${it.state}`}>
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${TONE[it.state]}`}>
                  {(it.state === 'uploading' || it.state === 'parsing') && <Loader2 className="w-3 h-3 animate-spin" />}
                  {it.state === 'succeeded' && <Check className="w-3 h-3" />}
                  {(it.state === 'failed' || it.state === 'unknown') && <X className="w-3 h-3" />}
                  {(it.state === 'duplicate' || it.state === 'review') && <AlertTriangle className="w-3 h-3" />}
                  {LABEL[it.state]}{it.state === 'uploading' ? ` ${it.uploadPct}%` : ''}
                </span>
                <span className="font-medium text-gray-900 truncate max-w-[280px]" title={it.name}>{it.name}</span>
                <span className="text-xs text-gray-400">{(it.size / 1024).toFixed(0)} kB</span>
                {it.invoiceNumber && <span className="text-xs text-gray-700">→ {it.invoiceNumber}</span>}
                {it.message && <span className="text-gray-600 basis-full sm:basis-auto">{it.message}</span>}
                {it.warnings?.length > 0 && <span className="text-xs text-amber-700">{it.warnings.length} parserwaarschuwing(en)</span>}
                {it.invoiceId && onOpenInvoice && (
                  <button onClick={() => onOpenInvoice(it.invoiceId)} className="text-xs text-gray-700 hover:text-gray-900 inline-flex items-center gap-1 underline" data-testid={`pdf-import-open-${it.invoiceNumber || it.invoiceId}`}>
                    <Eye className="w-3 h-3" /> Bekijk factuur
                  </button>
                )}
                {!running && it.state === 'failed' && (
                  <button onClick={() => retryOne(it.id)} className="text-xs inline-flex items-center gap-1 underline text-gray-700" data-testid="pdf-import-retry-btn"><RefreshCw className="w-3 h-3" /> Opnieuw</button>
                )}
                {!running && it.state === 'unknown' && (
                  <button onClick={() => reconcileAndRetry(it)} className="text-xs inline-flex items-center gap-1 underline text-gray-700" data-testid="pdf-import-reconcile-btn"><RefreshCw className="w-3 h-3" /> Controleer & opnieuw</button>
                )}
              </li>
            ))}
          </ul>
          {allDone && (
            <p className="mt-3 text-sm text-gray-800" data-testid="pdf-import-summary">
              Klaar: {counts.succeeded || 0} geïmporteerd, {counts.review || 0} nazicht, {counts.duplicate || 0} duplicaat, {counts.failed || 0} mislukt, {counts.unknown || 0} onbekend, {counts.rejected || 0} geweigerd, {counts.stopped || 0} niet gestart. Geïmporteerde facturen staan in de lijst met status <em>wacht op export</em>.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
